import Close from '@mui/icons-material/Close';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AddOnRateType,
  getV1CompaniesCompanyIdAddOnsId,
  getV1CompaniesCompanyIdAddOnsTypeahead,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { AddOn, AddOnCharge, AddOnTypeahead } from '~hooks/useAddOns/models';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { formatPercentage } from '~utils/utilFunctions';

import { AddOnChargesDTO, AddOnChargesFormHandler } from '../../AddOnChargesTableRows';
import { appendAddOnRateTypeToName, invoiceHelpers, schemaHelpers } from '../../helpers';
import { GridConfigType } from '../SettlementDetails/constants';

// Helper function to format delta with signs and colors
const formatDelta = (delta: number, currencyFormatter: (value: number) => string) => {
  const formattedValue = currencyFormatter(Math.abs(delta));

  if (delta === 0) {
    return { formattedValue, sign: '', color: 'inherit' };
  }

  const sign = delta > 0 ? '+' : '-';
  const color = delta > 0 ? 'success.main' : 'error.main';

  return { formattedValue, sign, color };
};

type RebillSettlementAddOnChargesProps = {
  settlementId: string;
  settlementAddOnCharges: AddOnCharge[];
  currencyFormatter: (value: number) => string;
  isEditing: boolean;
  gridConfig: GridConfigType;
};

const RebillSettlementAddOnCharges = forwardRef<
  AddOnChargesFormHandler,
  RebillSettlementAddOnChargesProps
>(function RebillSettlementAddOnCharges(
  { settlementAddOnCharges, currencyFormatter, isEditing, gridConfig },
  ref,
) {
  const theme = useTheme();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useFormContext<AddOnChargesDTO>();

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'addOnCharges',
  });

  const watchedAddOnCharges = useWatch({ control, name: 'addOnCharges' });

  const handleAppendAddOn = useCallback(() => {
    const addOnCharge = new AddOnCharge(
      null,
      '',
      0,
      0,
      0,
      0,
      AddOnRateType.RATE_FOR_EACH,
      null,
      { id: '' },
      null,
      null,
      null,
      null,
      null,
    );

    append(schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge));
  }, [append]);

  const fetchAddOns = useCallback(
    async (additionalProps: { query?: string; cursor?: string } = {}) => {
      try {
        const response = await getV1CompaniesCompanyIdAddOnsTypeahead({
          path: { 'company-id': companyId },
          query: {
            'search[query]': additionalProps.query || undefined,
            'page[limit]': DEFAULT_API_PAGE_SIZE,
            ...(additionalProps.cursor ? { 'page[after]': additionalProps.cursor } : {}),
          },
        });

        const addOns = response.data.data.map((addOn) => {
          const parsed = AddOnTypeahead.parse(addOn);

          return {
            id: parsed.id,
            name: appendAddOnRateTypeToName(parsed.name, parsed.addOnRateType),
            addOnRateType: parsed.addOnRateType,
          };
        });

        return { data: addOns, pagination: extractPagination(response) };
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.add_ons.failed_to_fetch'));
        return { data: [], pagination: { after: '', before: '' } };
      }
    },
    [companyId],
  );

  const fetchAddOnDetails = useCallback(
    async (addOnId: string) => {
      try {
        const response = await getV1CompaniesCompanyIdAddOnsId({
          path: { 'company-id': companyId, id: addOnId },
        });

        return AddOn.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.add_ons.failed_to_fetch'));
        return null;
      }
    },
    [companyId],
  );

  const handleRemoveAddOnClick = useCallback(
    (index: number) => {
      const addOnChargeToDelete = watchedAddOnCharges?.[index];

      if (addOnChargeToDelete?.id) {
        update(index, { ...addOnChargeToDelete, _destroy: 1 });
      } else {
        remove(index);
      }
    },
    [update, watchedAddOnCharges],
  );

  useImperativeHandle(
    ref,
    () => ({
      onSubmit: () => {
        return new Promise((resolve) => {
          handleSubmit((data) => {
            resolve(data.addOnCharges ?? []);
          })();
        });
      },
      append: handleAppendAddOn,
    }),
    [],
  );

  if (isEditing) {
    return (
      <>
        {fields.map((field, index) => {
          const isDeleted = watchedAddOnCharges?.[index]?._destroy === 1;

          if (isDeleted) {
            return null;
          }

          const editedAddOnCharge = watchedAddOnCharges?.[index];
          const originalAddOnCharge = settlementAddOnCharges.find(
            (charge) => charge.id === editedAddOnCharge?.id,
          );
          const isAddOnSelected = Boolean(editedAddOnCharge?.addOn?.id);
          const isNonRatePercentOfTotal =
            editedAddOnCharge?.addOnRateType === AddOnRateType.RATE_FOR_EACH ||
            editedAddOnCharge?.addOnRateType === AddOnRateType.PER_LOAD ||
            (editedAddOnCharge?.addOnRateType === AddOnRateType.FUEL_SURCHARGE &&
              !editedAddOnCharge?.isPercentage);

          const quantity = editedAddOnCharge?.quantity;
          const rate = editedAddOnCharge?.rebillValue || editedAddOnCharge?.value;

          return (
            <Box
              key={field.id}
              sx={{
                ...gridConfig,
                borderBottom: (theme) => `1px solid ${theme.semantic.border}`,
              }}
            >
              <AutocompleteAsyncFormField
                clearable
                control={control}
                errors={errors}
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                isRequired
                name={`addOnCharges[${index}].addOn`}
                asyncCallback={fetchAddOns}
                label=""
                sx={{
                  gridColumn: '1 / 4',
                  fontSize: '12px',
                  '& .MuiAutocomplete-inputRoot': { fontSize: '12px', width: '100%' },
                  '& .MuiAutocomplete-endAdornment': {
                    right: '5px !important',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 'fit-content',
                  },
                }}
                onSelect={async (selectedOption) => {
                  // Update the add-on rate type when an option is selected
                  if (selectedOption && selectedOption.addOnRateType) {
                    // Fetch the full add-on details to get default values
                    const addOnDetails = await fetchAddOnDetails(selectedOption.id);

                    if (addOnDetails) {
                      // Determine if this is a percentage-based add-on
                      const isPercentageBased =
                        addOnDetails.revenueCostRateType ===
                          AddOnRateType.RATE_PERCENT_OF_TOTAL ||
                        (addOnDetails.revenueCostRateType ===
                          AddOnRateType.FUEL_SURCHARGE &&
                          addOnDetails.percentage > 0);

                      // Determine if this is a quantity-based add-on
                      const isQuantityBased =
                        addOnDetails.revenueCostRateType ===
                          AddOnRateType.RATE_FOR_EACH ||
                        addOnDetails.revenueCostRateType === AddOnRateType.PER_LOAD ||
                        (addOnDetails.revenueCostRateType ===
                          AddOnRateType.FUEL_SURCHARGE &&
                          addOnDetails.percentage === 0);

                      update(index, {
                        ...editedAddOnCharge,
                        addOnRateType: selectedOption.addOnRateType as AddOnRateType,
                        addOn: {
                          id: selectedOption.id,
                          name: selectedOption.name,
                        },
                        quantity: isQuantityBased ? addOnDetails.quantity || 0 : 0,
                        value: isPercentageBased
                          ? addOnDetails.percentage || 0
                          : addOnDetails.rate || 0,
                        rebillValue: isPercentageBased
                          ? addOnDetails.percentage || 0
                          : addOnDetails.rate || 0,
                        load: editedAddOnCharge?.load || { id: undefined },
                      });
                    } else {
                      // Fallback to basic update if fetch fails
                      update(index, {
                        ...editedAddOnCharge,
                        addOnRateType: selectedOption.addOnRateType as AddOnRateType,
                        addOn: {
                          id: selectedOption.id,
                          name: selectedOption.name,
                        },
                        quantity: editedAddOnCharge?.quantity || 0,
                        value: editedAddOnCharge?.value || 0,
                        rebillValue: editedAddOnCharge?.rebillValue || 0,
                        load: editedAddOnCharge?.load || { id: undefined },
                      });
                    }
                  }
                  return selectedOption;
                }}
              />
              {isAddOnSelected && isNonRatePercentOfTotal && (
                <TextFormField
                  control={control}
                  errors={errors}
                  type="number"
                  name={`addOnCharges[${index}].quantity`}
                  sx={{
                    ml: 'auto',
                    '& .MuiFormControl-root': { mb: 0 },
                    '& .MuiOutlinedInput-root': { pr: 0 },
                    '& .MuiOutlinedInput-input': { p: '5px 7px', fontSize: '12px' },
                    gridArea: 'qty',
                    '& .MuiInputBase-input': {
                      fontSize: '12px',
                    },
                  }}
                  inputProps={{ sx: { textAlign: 'right' } }}
                />
              )}
              {isAddOnSelected ? (
                <TextFormField
                  control={control}
                  errors={errors}
                  type="number"
                  name={`addOnCharges[${index}].rebillValue`}
                  sx={{
                    ml: 'auto',
                    '& .MuiFormControl-root': { mb: 0 },
                    '& .MuiOutlinedInput-root': { pr: 0, pl: '7px' },
                    '& .MuiOutlinedInput-input': {
                      p: '5px 7px 5px 0px',
                      fontSize: '12px',
                    },
                    gridArea: 'rate',
                    '& .MuiInputBase-input': {
                      fontSize: '12px',
                    },
                  }}
                  inputProps={{ sx: { textAlign: 'right' } }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        sx={{
                          mr: '5px',
                          fontSize: '12px',
                        }}
                      >
                        {isNonRatePercentOfTotal ? '$' : '%'}
                      </Typography>
                    ),
                  }}
                />
              ) : null}
              <Box sx={{ gridArea: 'total', fontSize: '12px' }}>
                {quantity && rate
                  ? (() => {
                      const dependsOnLineItems =
                        invoiceHelpers.addOnTotalDependsOnLineItems(
                          editedAddOnCharge?.addOnRateType as AddOnRateType | null,
                        );
                      const shouldRecompute =
                        dependsOnLineItems ||
                        invoiceHelpers.hasAddOnChargeEdits(
                          editedAddOnCharge,
                          originalAddOnCharge,
                        );
                      const originalTotal = shouldRecompute
                        ? invoiceHelpers.calculateAddOnChargeTotal(
                            editedAddOnCharge?.addOnRateType
                              ? (editedAddOnCharge.addOnRateType as AddOnRateType)
                              : null,
                            editedAddOnCharge?.quantity,
                            editedAddOnCharge?.value || (originalAddOnCharge?.rate ?? 0),
                            editedAddOnCharge?.isPercentage
                              ? editedAddOnCharge?.value
                              : 0,
                            0,
                            0,
                          )
                        : (originalAddOnCharge?.totalAmount ?? 0);
                      return originalTotal >= 0
                        ? currencyFormatter(originalTotal)
                        : `(${currencyFormatter(-originalTotal)})`;
                    })()
                  : '$-'}
              </Box>
              <Box sx={{ gridArea: 'rebillTotal', fontSize: '12px' }}>
                {quantity && rate
                  ? (() => {
                      const dependsOnLineItems =
                        invoiceHelpers.addOnTotalDependsOnLineItems(
                          editedAddOnCharge?.addOnRateType as AddOnRateType | null,
                        );
                      // When the add-on has never been rebilled, the backend
                      // leaves rebillTotalAmount null. Force recompute so the
                      // OR-chain below substitutes the original rate and shows
                      // the inherited total instead of falling back to $0.
                      const shouldRecompute =
                        dependsOnLineItems ||
                        isNil(originalAddOnCharge?.rebillTotalAmount) ||
                        invoiceHelpers.hasAddOnChargeEdits(
                          editedAddOnCharge,
                          originalAddOnCharge,
                          true,
                        );
                      const rebillTotal = shouldRecompute
                        ? invoiceHelpers.calculateRebillAddOnChargeTotal(
                            editedAddOnCharge?.addOnRateType
                              ? (editedAddOnCharge.addOnRateType as AddOnRateType)
                              : null,
                            editedAddOnCharge?.quantity,
                            editedAddOnCharge?.rebillValue ||
                              originalAddOnCharge?.rebillRevenueRateValue ||
                              editedAddOnCharge?.value,
                            editedAddOnCharge?.isPercentage
                              ? editedAddOnCharge?.rebillValue || editedAddOnCharge?.value
                              : 0,
                            0,
                            0,
                          )
                        : (originalAddOnCharge?.rebillTotalAmount ?? 0);
                      return rebillTotal >= 0
                        ? currencyFormatter(rebillTotal)
                        : `(${currencyFormatter(-rebillTotal)})`;
                    })()
                  : '$-'}
              </Box>
              <Box sx={{ gridArea: 'delta', fontSize: '12px' }}>{'—'}</Box>
              <Tooltip title={t('actions.remove')}>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveAddOnClick(index)}
                  sx={{ gridArea: 'delta', justifySelf: 'end' }}
                >
                  <Close
                    fontSize="small"
                    sx={{ color: theme.brandV2.colors.treadGray3 }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </>
    );
  }
  return settlementAddOnCharges.map((addOnCharge) => (
    <Box
      key={addOnCharge.id}
      sx={{
        ...gridConfig,
        borderBottom: (theme) => `1px solid ${theme.semantic.border}`,
        backgroundColor: (theme) => {
          if (
            addOnCharge.rebillTotalAmount === null ||
            addOnCharge.rebillTotalAmount === undefined
          ) {
            return 'transparent';
          }
          const delta = addOnCharge.rebillTotalAmount - addOnCharge.totalAmount;
          return delta !== 0 ? theme.semantic.chipBrandBg : 'transparent';
        },
      }}
    >
      <Typography variant="body2" sx={{ gridColumn: '1 / 5' }}>
        {`${t('approvals.driver_pay.settlements.add_on_name_prefixed', { name: addOnCharge.name })}`}
      </Typography>
      {!!addOnCharge.quantity && (
        <Typography variant="body2" sx={{ gridArea: 'qty' }}>
          {addOnCharge.quantity}
        </Typography>
      )}
      {addOnCharge.percentage ? (
        <Typography variant="body2" sx={{ gridArea: 'rate' }}>
          {formatPercentage(addOnCharge.rebillPercentage)}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ gridArea: 'rate' }}>
          {(addOnCharge.rebillRevenueRateValue || 0) >= 0
            ? currencyFormatter(addOnCharge.rebillRevenueRateValue || 0)
            : `(${currencyFormatter(-(addOnCharge.rebillRevenueRateValue || 0))})`}
        </Typography>
      )}
      <Typography variant="body2" sx={{ gridArea: 'total' }}>
        {addOnCharge.totalAmount >= 0
          ? currencyFormatter(addOnCharge.totalAmount)
          : `(${currencyFormatter(-addOnCharge.totalAmount)})`}
      </Typography>

      <Typography variant="body2" sx={{ gridArea: 'rebillTotal' }}>
        {addOnCharge.rebillTotalAmount !== null &&
        addOnCharge.rebillTotalAmount !== undefined
          ? addOnCharge.rebillTotalAmount >= 0
            ? currencyFormatter(addOnCharge.rebillTotalAmount)
            : `(${currencyFormatter(-addOnCharge.rebillTotalAmount)})`
          : '$-'}
      </Typography>

      <Typography variant="body2" sx={{ gridArea: 'delta' }}>
        {addOnCharge.rebillTotalAmount !== null &&
        addOnCharge.rebillTotalAmount !== undefined
          ? (() => {
              const delta = addOnCharge.rebillTotalAmount - addOnCharge.totalAmount;
              const { sign, formattedValue, color } = formatDelta(
                delta,
                currencyFormatter,
              );
              return (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color,
                    fontWeight: 'medium',
                  }}
                >
                  {sign}
                  {formattedValue}
                </Typography>
              );
            })()
          : '$-'}
      </Typography>

      {isEditing && (
        <IconButton
          type="button"
          onClick={() => {}}
          sx={{ gridArea: 'action', justifySelf: 'end' }}
        >
          <CloseIcon fontSize={'small'} />
        </IconButton>
      )}
    </Box>
  ));
});

export default observer(RebillSettlementAddOnCharges);
